/**
 * Per-outlet print routing. A printer record says what it prints:
 *   roles: { receipt: bool, token: bool, kotStations: ['kitchen', 'beverages', …] or ['*'] }
 *
 * KOT rule: printers for that station → printers marked "all stations" (*) → the receipt
 * printer (single-printer counters need no extra setup). No printer → browser print dialog.
 */
const active = (printers) => (printers ?? []).filter((p) => p.isActive !== false);

export function receiptPrinter(printers) {
  return active(printers).find((p) => p.roles?.receipt) ?? null;
}

export function tokenPrinter(printers) {
  const list = active(printers);
  return list.find((p) => p.roles?.token) ?? receiptPrinter(list);
}

export function kotPrinters(printers, station = 'kitchen') {
  const list = active(printers);
  const exact = list.filter((p) => p.roles?.kotStations?.includes(station));
  if (exact.length) return exact;
  const all = list.filter((p) => p.roles?.kotStations?.includes('*'));
  if (all.length) return all;
  const r = receiptPrinter(list);
  return r ? [r] : [];
}

export const PRINTER_VIA = Object.freeze({
  agent: 'Print agent',
  device: 'This device (USB / serial / Bluetooth)',
});

export const AGENT_CONNECTIONS = Object.freeze({
  network: 'Network (LAN / Wi-Fi, port 9100)',
  windows: 'Windows printer (USB, via the spooler in RAW mode)',
  serial: 'Serial / COM port',
  usb: 'USB device file (Linux /dev/usb/lp0)',
  cups: 'CUPS printer (macOS / Linux, raw)',
});

export const DEVICE_APIS = Object.freeze({
  webusb: 'USB (WebUSB)',
  webserial: 'Serial (Web Serial)',
  webbluetooth: 'Bluetooth LE (Web Bluetooth)',
});

/** One-line description of how a printer is connected, for lists and the test page. */
export function connectionLabel(p) {
  const c = p?.connection ?? {};
  if (c.via === 'agent') {
    const agent = p.agentName ? `${p.agentName}: ` : '';
    if (c.kind === 'network') return `${agent}${c.host}:${c.port ?? 9100}`;
    if (c.kind === 'windows' || c.kind === 'cups') return `${agent}${c.printerName}`;
    if (c.kind === 'serial') return `${agent}${c.path} @ ${c.baudRate ?? 9600}`;
    if (c.kind === 'usb') return `${agent}${c.path}`;
    return `${agent}agent`;
  }
  if (c.via === 'device') return `${DEVICE_APIS[c.api] ?? c.api}${p.deviceName ? ` on ${p.deviceName}` : ''}`;
  return 'Not set';
}
